import { Theme } from "@mui/material";
import { makeStyles } from "@mui/styles";

const FIELD_MAX_WIDTH = 377;
const INPUT_HEIGHT = 48;
const INPUT_PADDING = "12px 16px 12px 8px";

export const useStyles = makeStyles((theme: Theme) => {
  const pageBackground = theme.palette.grey[400];
  const fieldInputText = {
    fontFamily: theme.typography.fontFamily,
    fontWeight: 400,
    fontSize: 14,
    lineHeight: "21px",
    letterSpacing: 0,
    textAlign: "right" as const,
    color: theme.palette.secondary.main,
  };

  const card = {
    width: "100%",
    backgroundColor: theme.palette.background.paper,
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    padding: 24,
    boxSizing: "border-box" as const,
    direction: "rtl" as const,
    textAlign: "right" as const,
    display: "flex",
    flexDirection: "column" as const,
    gap: 16,
  };

  const inputRoot = {
    width: "100%",
    height: INPUT_HEIGHT,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 4,
    gap: 6,
    padding: INPUT_PADDING,
  };

  return {
    container: {
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      background: pageBackground,
      direction: "rtl",
      overflow: "hidden",
    },

    contentWrapper: {
      flex: 1,
      minWidth: 0,
      height: "100%",
      display: "flex",
      flexDirection: "column",
      background: pageBackground,
      overflow: "hidden",
    },

    topbarWrapper: {
      width: "100%",
      flexShrink: 0,
      position: "sticky",
      top: 0,
      zIndex: 100,
      background: theme.palette.background.paper,
    },

    scrollContent: {
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      overflowX: "hidden",
      padding: 0,
      boxSizing: "border-box",
      background: pageBackground,
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      "&::-webkit-scrollbar": {
        display: "none",
      },
    },

    pageContent: {
      display: "flex",
      flexDirection: "column",
      gap: 16,
      opacity: 1,
      marginTop: 10,
      marginLeft: 24,
      marginRight: 24,
      marginBottom: 24,
      boxSizing: "border-box",
    },

    profileCard: {
      ...card,
    },

    preferencesCard: {
      ...card,
      alignItems: "flex-start",
    },

    formField: {
      width: "100%",
      maxWidth: FIELD_MAX_WIDTH,
    },

    avatarSection: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
    },

    fieldLabel: {
      fontFamily: theme.typography.fontFamily,
      fontWeight: 500,
      fontSize: 12,
      lineHeight: "140%",
      letterSpacing: 0,
      textAlign: "right",
      color: theme.palette.text.primary,
      marginBottom: 8,
      width: "100%",
    },

    textField: {
      width: FIELD_MAX_WIDTH,
      maxWidth: "100%",
      margin: 0,
      "& .MuiOutlinedInput-root": inputRoot,
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.divider,
      },
      "& .MuiOutlinedInput-input, & .MuiSelect-select": {
        ...fieldInputText,
        padding: 0,
        height: "100%",
        boxSizing: "border-box",
      },
      "& .MuiSelect-select": {
        display: "flex",
        alignItems: "center",
      },
      "& .MuiInputAdornment-root": {
        margin: 0,
      },
    },

    preferencesSelect: {
      "& .MuiOutlinedInput-root": {
        direction: "ltr",
        display: "flex",
        alignItems: "center",
      },
      "& .MuiSelect-icon, & .MuiSelect-iconOpen": {
        right: "auto",
        left: 8,
        marginRight: 0,
        color: theme.palette.text.primary,
        width: 20,
        height: 20,
      },
      "& .MuiOutlinedInput-root .MuiSelect-select.MuiSelect-outlined": {
        ...fieldInputText,
        padding: 0,
        direction: "rtl",
        width: "100%",
        minHeight: "unset",
        display: "block",
        boxSizing: "border-box",
      },
    },

    fieldIcon: {
      color: theme.palette.secondary.main,
    },

    avatar: {
      width: 82,
      height: 82,
      borderRadius: "50%",
      backgroundColor: theme.palette.profile.avatarBackground,
      color: theme.palette.profile.avatarText,
      fontWeight: 600,
      fontSize: 18,
    },

    passwordSection: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
    },

    passwordRow: {
      display: "flex",
      alignItems: "flex-end",
      gap: 16,
      width: "auto",
      direction: "ltr",
    },

    passwordFieldWrapper: {
      width: FIELD_MAX_WIDTH,
      maxWidth: "100%",
      flexShrink: 0,
      direction: "rtl",
      textAlign: "right",
    },

    changePasswordButton: {
      fontFamily: theme.typography.fontFamily,
      fontWeight: 500,
      fontSize: 16,
      lineHeight: "24px",
      letterSpacing: 0,
      width: 216,
      height: INPUT_HEIGHT,
      minWidth: 216,
      borderColor: theme.palette.text.primary,
      color: theme.palette.text.primary,
      borderRadius: 4,
      padding: "15px 24px",
      textTransform: "none",
      flexShrink: 0,
      boxSizing: "border-box",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      "&.MuiButton-outlined": {
        borderWidth: 1,
        borderColor: theme.palette.text.primary,
      },
      "&.MuiButton-outlined:hover": {
        borderColor: theme.palette.text.primary,
      },
    },
  };
});
